import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { 
  catchError, 
  map, 
  switchMap, 
  takeUntil, 
  tap,
  withLatestFrom,
  filter,
  debounceTime,
  mergeMap,
  exhaustMap
} from 'rxjs/operators';
import { of, EMPTY, merge } from 'rxjs';
import { WebSocketService } from '../../core/services/websocket.service';
import { RealTimePriceService } from '../../core/services/real-time-price.service';
import { WebSocketActions, RealTimePriceActions } from '../actions/websocket.actions';
import { DashboardActions } from '../actions/dashboard.actions';

@Injectable()
export class WebSocketEffects {
  
  // Connection Effects
  connect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebSocketActions.connect),
      tap(({ token }) => {
        this.webSocketService.connect(token);
      }),
      switchMap(() => 
        this.webSocketService.connectionStatus.pipe(
          map(status => 
            status.isConnected 
              ? WebSocketActions.connectSuccess()
              : WebSocketActions.connectionStatusChanged({ status })
          ),
          catchError(error => of(WebSocketActions.connectFailure({ error })))
        )
      )
    )
  );

  disconnect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebSocketActions.disconnect),
      tap(() => {
        this.webSocketService.disconnect();
      })
    ),
    { dispatch: false }
  );

  connectionStatusChanged$ = createEffect(() =>
    this.webSocketService.connectionStatus.pipe(
      map(status => WebSocketActions.connectionStatusChanged({ status }))
    )
  );

  // Subscription Effects
  subscribeToProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebSocketActions.subscribeToProducts),
      tap(({ productIds }) => {
        this.realTimePriceService.subscribeToProducts(productIds);
      })
    ),
    { dispatch: false }
  );

  unsubscribeFromProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebSocketActions.unsubscribeFromProducts),
      tap(({ productIds }) => {
        productIds.forEach(id => this.realTimePriceService.unsubscribeFromProduct(id));
      })
    ),
    { dispatch: false }
  );

  subscribeToDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebSocketActions.subscribeToDashboard),
      tap(() => {
        this.webSocketService.subscribeToDashboard();
      })
    ),
    { dispatch: false }
  );

  // Real-time Data Effects
  priceUpdates$ = createEffect(() =>
    this.webSocketService.priceUpdates.pipe(
      debounceTime(100), // Batch updates for performance
      map(updates => WebSocketActions.priceUpdateReceived({ updates }))
    )
  );

  competitorPrices$ = createEffect(() =>
    this.webSocketService.competitorPrices.pipe(
      map(update => WebSocketActions.competitorPriceReceived({ update }))
    )
  );

  priceAlerts$ = createEffect(() =>
    this.webSocketService.priceAlerts.pipe(
      map(alert => WebSocketActions.priceAlertReceived({ alert }))
    )
  );

  marketChanges$ = createEffect(() =>
    this.webSocketService.marketChanges.pipe(
      map(change => WebSocketActions.marketChangeReceived({ change }))
    )
  );

  analyticsUpdates$ = createEffect(() =>
    this.webSocketService.analyticsUpdates.pipe(
      map(update => WebSocketActions.analyticsUpdateReceived({ update }))
    )
  );

  systemAlerts$ = createEffect(() =>
    this.webSocketService.systemAlerts.pipe(
      map(alert => WebSocketActions.systemAlertReceived({ alert }))
    )
  );

  // Process Real-time Price Updates
  processPriceUpdates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebSocketActions.priceUpdateReceived),
      switchMap(({ updates }) => 
        this.realTimePriceService.getAllProductPrices().pipe(
          map(prices => RealTimePriceActions.updateProductPrices({ prices }))
        )
      )
    )
  );

  processCompetitorPrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebSocketActions.competitorPriceReceived),
      map(({ update }) => 
        RealTimePriceActions.updateCompetitorPrices({ 
          productId: update.productId, 
          competitors: [update] 
        })
      )
    )
  );

  processPriceAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebSocketActions.priceAlertReceived),
      mergeMap(({ alert }) => [
        RealTimePriceActions.addPriceAlert({ 
          alert: {
            id: alert.id,
            productId: alert.productId,
            type: alert.alertType,
            severity: alert.severity,
            message: alert.message,
            currentPrice: alert.currentPrice,
            targetPrice: alert.targetPrice,
            competitorPrice: alert.competitorPrice,
            timestamp: alert.timestamp,
            acknowledged: false
          }
        }),
        // Also update dashboard if it's a critical alert
        ...(alert.severity === 'critical' ? [
          DashboardActions.refreshMetrics()
        ] : [])
      ])
    )
  );

  processMarketChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebSocketActions.marketChangeReceived),
      switchMap(() => 
        this.realTimePriceService.getMarketMetrics().pipe(
          map(metrics => RealTimePriceActions.updateMarketMetrics({ metrics }))
        )
      )
    )
  );

  processAnalyticsUpdates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebSocketActions.analyticsUpdateReceived),
      map(({ update }) => {
        // Update dashboard metrics based on analytics
        if (update.type === 'revenue' || update.type === 'profit') {
          return DashboardActions.metricsUpdate({ 
            metrics: {
              revenue: update.data['totalRevenue'],
              profitMargin: update.data['profitMargin']
            }
          });
        }
        return { type: 'NO_ACTION' }; // No-op action
      }),
      filter(action => action.type !== 'NO_ACTION')
    )
  );

  // Real-time Price Service Effects
  subscribeToProductMonitoring$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimePriceActions.subscribeToProductMonitoring),
      tap(({ productId }) => {
        this.realTimePriceService.subscribeToProduct(productId);
      })
    ),
    { dispatch: false }
  );

  subscribeToMultipleProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimePriceActions.subscribeToMultipleProducts),
      tap(({ productIds }) => {
        this.realTimePriceService.subscribeToProducts(productIds);
      })
    ),
    { dispatch: false }
  );

  acknowledgeAlert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimePriceActions.acknowledgeAlert),
      tap(({ alertId }) => {
        this.realTimePriceService.acknowledgeAlert(alertId);
      })
    ),
    { dispatch: false }
  );

  clearAcknowledgedAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimePriceActions.clearAcknowledgedAlerts),
      tap(() => {
        this.realTimePriceService.clearAcknowledgedAlerts();
      })
    ),
    { dispatch: false }
  );

  // Sync Real-time Service State with Store
  syncPriceAlerts$ = createEffect(() =>
    this.realTimePriceService.getPriceAlerts().pipe(
      map(alerts => RealTimePriceActions.updatePriceAlerts({ alerts }))
    )
  );

  syncMarketMetrics$ = createEffect(() =>
    this.realTimePriceService.getMarketMetrics().pipe(
      map(metrics => RealTimePriceActions.updateMarketMetrics({ metrics }))
    )
  );

  // Error Handling
  webSocketError$ = createEffect(() =>
    merge(
      this.webSocketService.connectionStatus.pipe(
        filter(status => !status.isConnected),
        map(() => WebSocketActions.webSocketError({ error: 'Connection lost' }))
      )
    )
  );

  // Auto-reconnect on critical errors
  autoReconnect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebSocketActions.connectFailure),
      exhaustMap(({ error }) => {
        // Attempt reconnection after 5 seconds for critical errors
        if (this.isCriticalError(error)) {
          return of(WebSocketActions.connect({})).pipe(
            // Add delay for reconnection attempt
            switchMap(action => of(action))
          );
        }
        return EMPTY;
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private webSocketService: WebSocketService,
    private realTimePriceService: RealTimePriceService
  ) {}

  private isCriticalError(error: any): boolean {
    // Define what constitutes a critical error that should trigger auto-reconnect
    const criticalErrors = ['network_error', 'server_error', 'timeout'];
    return criticalErrors.some(criticalError => 
      error?.message?.toLowerCase().includes(criticalError)
    );
  }
}